/** Shared `button` recipe (Chakra v3) for text-bearing button controls. */

import { defineRecipe } from '@chakra-ui/react';

import type { RecipeVariantProps } from '@chakra-ui/react';

export const buttonRecipe = defineRecipe({
  className: 'button',

  base: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2xs',
    fontWeight: 'regular',
    borderRadius: 'l',
    cursor: 'pointer',
    transitionProperty: 'background-color, border-color, color, box-shadow, opacity',
    transitionDuration: '0.2s',
    _disabled: {
      cursor: 'not-allowed',
      opacity: 'disabled',
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'text.info',
      outlineOffset: '-2px', // effectively sets the focus outline as "inside" behavior
    },
    _icon: {
      flexShrink: 0,
      boxSize: 'icon.s',
    },
  },

  variants: {
    variant: {
      /** Primary solid (accent). */
      primary: {
        bg: 'background.accent',
        color: 'text.onAccent',
        borderColor: 'background.accent',
        borderWidth: '1px',
        borderStyle: 'solid',
        _hover: {
          bg: 'background.accent.hover',
          borderColor: 'background.accent.hover',
        },
        _active: {
          bg: 'background.accent.active',
          borderColor: 'background.accent.active',
        },
        _disabled: {
          bg: 'background.accent',
          borderColor: 'background.accent',
          color: 'text.onAccent',
        },
      },

      /** Secondary filled: grey border, green border on hover/active. */
      secondary: {
        bg: 'background.primary',
        color: 'text.primary',
        borderColor: 'border.accent',
        borderWidth: '1px',
        borderStyle: 'solid',
        _hover: {
          bg: 'background.primary',
          borderColor: 'border.accent.active',
          color: 'text.primary',
        },
        _active: {
          bg: 'background.subtle.green',
          borderColor: 'border.accent.active',
          color: 'text.primary',
        },
        _disabled: {
          bg: 'background.primary',
          borderColor: 'border.accent',
          color: 'text.primary',
        },
      },

      /** Secondary outline (accent text); no extra focus ring (border carries affordance). */
      secondaryOutline: {
        bg: 'background.primary',
        color: 'text.accent',
        borderColor: 'border.accent.active',
        borderWidth: '1px',
        borderStyle: 'solid',
        _hover: {
          bg: 'background.subtle.green',
          borderColor: 'border.accent.active',
          color: 'text.accent',
        },
        _active: {
          bg: 'background.subtle.green',
          borderColor: 'border.accent.active',
          color: 'text.accent',
        },
        _focusVisible: {
          outline: 'none',
        },
        _focusWithin: {
          outline: 'none',
        },
        _disabled: {
          bg: 'background.primary',
          borderColor: 'border.accent',
          color: 'text.primary',
        },
      },

      /** Tertiary: transparent, canvas bg on hover/active. */
      tertiary: {
        bg: 'transparent',
        color: 'text.primary',
        border: 'none',
        borderRadius: 'm',
        _hover: {
          bg: 'background.canvas',
          color: 'text.primary',
        },
        _active: {
          bg: 'background.canvas',
          color: 'text.primary',
        },
        _disabled: {
          color: 'text.primary',
        },
      },

      /** Tertiary with info-colored text (links / inline actions). */
      tertiaryAction: {
        bg: 'transparent',
        color: 'text.info',
        border: 'none',
        borderRadius: 'm',
        padding: '0',
        _hover: {
          color: 'text.info.hover',
          bg: 'transparent',
        },
        _active: {
          color: 'text.info.active',
        },
        _disabled: {
          color: 'text.info',
        },
      },

      /** Solid destructive. */
      destructive: {
        bg: 'background.error',
        color: 'text.onAccent',
        borderColor: 'background.error',
        borderWidth: '1px',
        borderStyle: 'solid',
        _hover: {
          bg: 'background.error.hover',
          borderColor: 'background.error.hover',
        },
        _active: {
          bg: 'background.error.active',
          borderColor: 'background.error.active',
        },
        _disabled: {
          bg: 'background.error',
          borderColor: 'background.error',
          color: 'text.onAccent',
        },
      },

      /** Destructive outline; red tint on hover/active only. */
      destructiveOutline: {
        bg: 'transparent',
        color: 'text.error',
        borderColor: 'border.error',
        borderWidth: '1px',
        borderStyle: 'solid',
        _hover: {
          bg: 'background.subtle.red',
          borderColor: 'border.error',
          color: 'text.error',
        },
        _active: {
          bg: 'background.subtle.red',
          borderColor: 'border.error',
          color: 'text.error',
        },
        _disabled: {
          bg: 'background.primary',
          color: 'text.error',
          borderColor: 'border.error',
        },
      },
    },
    size: {
      m: {
        textStyle: 'button.s',
        px: 'calc(var(--chakra-spacing-m) - 1px)',
        py: 'calc(var(--chakra-spacing-s) - 1px)',
      },
      l: {
        textStyle: 'button.m',
        px: 'calc(var(--chakra-spacing-m) - 1px)',
        py: 'calc(var(--chakra-spacing-m) - 1px)',
      },
    },
  },

  defaultVariants: {
    variant: 'primary',
    size: 'm',
  },
});

export type ButtonRecipeProps = RecipeVariantProps<typeof buttonRecipe>;

/** Same as recipe props but without `variant` / `size`. */
export type ButtonRecipePassthroughProps = Omit<ButtonRecipeProps, 'variant' | 'size'>;
